import { apiClient } from '@/lib/api';
import { supabase } from '@/lib/supabase';

/**
 * Reconcile `user_profiles.mfa_enabled` with Supabase's ground truth
 * (`auth.mfa_factors`). Non-blocking, idempotent — safe to call on every
 * login / MFA-verify handshake.
 *
 * Why this exists: `mfa_enabled` gates admin UI (Reset MFA button, Team
 * Members filters) but Supabase's factor table is the actual source of
 * truth used by SignIn / ProtectedRoute. The two can drift when:
 *   - Admin resets a user's factor (Supabase side) but the flag flip
 *     PATCH failed / hadn't shipped yet.
 *   - A user enrolls MFA against an older client that didn't PATCH.
 *   - A BE migration touched one side but not the other.
 *
 * @param {Object} profile - Currently loaded userprofile (needs
 *   userprofile_id + mfa_enabled). Falsy skips.
 * @param {boolean} [hasVerifiedTotpHint] - Optional pre-computed truth
 *   from a caller that already ran listFactors(). Saves a round trip.
 */
export async function syncMfaEnabled(profile, hasVerifiedTotpHint) {
  if (!profile?.userprofile_id) return;
  try {
    let hasVerifiedTotp = hasVerifiedTotpHint;
    if (hasVerifiedTotp === undefined) {
      const { data } = await supabase.auth.mfa.listFactors();
      // SDK versions split factors between `totp` and `all`. Reading
      // both keeps the check version-tolerant, matching SignIn.jsx.
      const totpFactors = [
        ...(data?.totp || []),
        ...((data?.all || []).filter((f) => f.factor_type === 'totp')),
      ];
      hasVerifiedTotp = totpFactors.some((f) => f.status === 'verified');
    }
    // Coerce both sides — the BE has some fields stored as loose types
    // (e.g. is_active stamped as the string 'TRUE' in some flows) so a
    // strict `=== true` could misfire and hammer PATCH on every login.
    if (Boolean(profile.mfa_enabled) === Boolean(hasVerifiedTotp)) return;
    await apiClient.updateUser(profile.userprofile_id, { mfa_enabled: Boolean(hasVerifiedTotp) });
  } catch (err) {
    console.warn('syncMfaEnabled: failed to reconcile mfa_enabled', err);
  }
}
